// UserContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import countryFoodPreferences from '../utils/country_food_preferences.json';
import { DEFAULT_COUNTRY, getCountryCurrency } from '../services/countryService';
import backendClient from '../services/backendClient';

const LOCAL_STORAGE_PREFERENCES_KEY = '9meals_preferences';

// Module-level flag to prevent double-fetch in React 18 StrictMode
let globalHasFetched = false;

// Default dietary option
const defaultDietaryPreference = { key: "none", label: "No preference", description: "No foods to avoid." };

// Default cooking style (standard complexity, no 2-day prep for each meal type)
const defaultCookingStyle = { 
  breakfast: { cookingTime: 'standard', cookFor2Days: false },
  lunch: { cookingTime: 'standard', cookFor2Days: false },
  dinner: { cookingTime: 'standard', cookFor2Days: false }
};

// Helper to migrate old cookingFrequency format to new cookingStyle format
const migrateCookingStyle = (data) => {
  // If it's already the new format, return as-is
  if (data?.breakfast?.cookingTime !== undefined) {
    return data;
  }
  
  // Migrate from old format { breakfast: 1, lunch: 1, dinner: 1 }
  if (typeof data?.breakfast === 'number') {
    return {
      breakfast: { cookingTime: 'standard', cookFor2Days: data.breakfast === 2 },
      lunch: { cookingTime: 'standard', cookFor2Days: data.lunch === 2 },
      dinner: { cookingTime: 'standard', cookFor2Days: data.dinner === 2 }
    };
  }
  
  return defaultCookingStyle;
};

const UserContext = createContext();

export const useUser = () => useContext(UserContext);

function getDefaultBudgetRange(country = DEFAULT_COUNTRY) {
  const countryData = countryFoodPreferences[country] || countryFoodPreferences[DEFAULT_COUNTRY];
  const currency = getCountryCurrency(country);
  if (!countryData || !countryData.budget) return null;
  if (!countryData.budget.standard) return null;
  const [min, max] = countryData.budget.standard;
  let label = `${currency.symbol}${min} - ${currency.symbol}${max}`;
  if (max === null) label = `${currency.symbol}${min}+`;
  return {
    key: 'standard', // Add the key field!
    min,
    max,
    label: `${label} • Standard`,
    currency: currency.symbol
  };
}

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [budget, setBudget] = useState(getDefaultBudgetRange()); // default budget as range object
  const [numberOfPeople, setNumberOfPeople] = useState(1); // default number of people
  const [selectedCuisine, setSelectedCuisine] = useState('Surprise Me'); // default cuisine
  const [dietaryPreferences, setDietaryPreferences] = useState([defaultDietaryPreference]); // default dietary preferences as array of objects
  const [cookingStyle, setCookingStyle] = useState(defaultCookingStyle); // default cooking style
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Load preferences from localStorage (for non-authenticated users)
  const loadLocalPreferences = useCallback(() => {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_PREFERENCES_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.numberOfPeople) {
          setNumberOfPeople(data.numberOfPeople);
        }
        if (data.budget && data.budget.key) {
          setBudget(data.budget);
        }
        if (data.selectedCuisine) {
          setSelectedCuisine(data.selectedCuisine);
        }
        if (data.dietaryPreferences && data.dietaryPreferences.length > 0) {
          setDietaryPreferences(data.dietaryPreferences);
        }
        // Handle both old cookingFrequency and new cookingStyle formats
        if (data.cookingStyle) {
          setCookingStyle(migrateCookingStyle(data.cookingStyle));
        } else if (data.cookingFrequency) {
          // Migrate from old format
          setCookingStyle(migrateCookingStyle(data.cookingFrequency));
        }
        console.log('[UserContext] Loaded preferences from localStorage:', data);
        return true;
      }
    } catch (error) {
      console.error('[UserContext] Error loading preferences from localStorage:', error);
    }
    return false;
  }, []);

  // Save preferences to localStorage (for non-authenticated users)
  // Accepts an optional explicitData object to bypass stale closure values (e.g. from handleSubmit)
  const saveLocalPreferences = useCallback((explicitData = null) => {
    try {
      const data = explicitData || {
        numberOfPeople,
        budget,
        selectedCuisine,
        dietaryPreferences,
        cookingStyle
      };
      localStorage.setItem(LOCAL_STORAGE_PREFERENCES_KEY, JSON.stringify(data));
      console.log('[UserContext] Preferences saved to localStorage');
      return true;
    } catch (error) {
      console.error('[UserContext] Error saving preferences to localStorage:', error);
      return false;
    }
  }, [numberOfPeople, budget, selectedCuisine, dietaryPreferences, cookingStyle]);

  // Fetch preferences from backend (for authenticated users)
  const fetchPreferences = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    
    // For non-authenticated users, load from localStorage
    if (!token) {
      loadLocalPreferences();
      setPreferencesLoaded(true);
      return;
    }

    // Fresh OAuth login: local preferences exist from anonymous session.
    // Push them to backend instead of pulling (empty) defaults from a new account.
    const hasLocalPrefs = localStorage.getItem(LOCAL_STORAGE_PREFERENCES_KEY);
    if (hasLocalPrefs) {
      const loaded = loadLocalPreferences();
      if (loaded) {
        setPreferencesLoaded(true);
        console.log('[UserContext] Fresh OAuth login — using local preferences, will push to backend via auto-save');
        return;
      }
    }

    try {
      const data = await backendClient.get('/api/user/preferences');

      if (data) {
        // Apply saved preferences
        if (data.numberOfPeople) {
          setNumberOfPeople(data.numberOfPeople);
        }
        if (data.budget && data.budget.key) {
          setBudget(data.budget);
        }
        if (data.selectedCuisine) {
          setSelectedCuisine(data.selectedCuisine);
        }
        if (data.dietaryPreferences && data.dietaryPreferences.length > 0) {
          setDietaryPreferences(data.dietaryPreferences);
        }
        // Handle both old cookingFrequency and new cookingStyle formats
        if (data.cookingStyle) {
          setCookingStyle(migrateCookingStyle(data.cookingStyle));
        } else if (data.cookingFrequency) {
          // Migrate from old format
          setCookingStyle(migrateCookingStyle(data.cookingFrequency));
        }
        setPreferencesLoaded(true);
        console.log('[UserContext] Loaded preferences from backend:', data);
      }
    } catch (error) {
      console.error('[UserContext] Error fetching preferences:', error);
    }
  }, [loadLocalPreferences]);

  // Save all current preferences (called explicitly on button click or from submit)
  // Uses backend API for authenticated users, localStorage for non-authenticated
  // Accepts an optional explicitData object to bypass stale closure values (e.g. from handleSubmit)
  const savePreferences = useCallback(async (explicitData = null) => {
    const token = localStorage.getItem('authToken');
    const data = explicitData || {
      numberOfPeople,
      budget,
      selectedCuisine,
      dietaryPreferences,
      cookingStyle
    };

    // For non-authenticated users, save to localStorage
    if (!token) {
      return saveLocalPreferences(data);
    }

    // For authenticated users, save to backend
    try {
      await backendClient.put('/api/user/preferences', data);
      console.log('[UserContext] Preferences saved to backend');
      return true;
    } catch (error) {
      console.error('[UserContext] Error saving preferences:', error);
      return false;
    }
  }, [numberOfPeople, budget, selectedCuisine, dietaryPreferences, cookingStyle, saveLocalPreferences]);

  // Load preferences on mount (for both authenticated and non-authenticated users)
  useEffect(() => {
    if (!globalHasFetched) {
      globalHasFetched = true;
      fetchPreferences();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save preferences whenever they change (after initial load)
  useEffect(() => {
    // Only auto-save after preferences have been loaded to avoid overwriting with defaults
    if (!preferencesLoaded) return;
    
    // Debounce the save to avoid too many writes
    const timeoutId = setTimeout(() => {
      savePreferences();
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [numberOfPeople, budget, selectedCuisine, dietaryPreferences, cookingStyle, preferencesLoaded, savePreferences]);

  // Reset preferences loaded state on logout
  const resetPreferencesLoaded = useCallback(() => {
    setPreferencesLoaded(false);
    globalHasFetched = false; // Allow re-fetch after logout
  }, []);

  const value = { 
    user,
    setUser,
    budget,
    setBudget,
    numberOfPeople,
    setNumberOfPeople,
    selectedCuisine,
    setSelectedCuisine,
    dietaryPreferences,
    setDietaryPreferences,
    cookingStyle,
    setCookingStyle,
    fetchPreferences,
    savePreferences,
    preferencesLoaded,
    resetPreferencesLoaded,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};